import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import Paper from "@mui/material/Paper"
import { useSnackbar } from "notistack"
import {
  assignClient,
  disablePractitioner,
  disableSuperAdmin,
  enableSuperAdmin,
  getPractitionerStatus,
  listClients,
  unassignClient,
  upsertPractitioner,
} from "services/practitioner"
import { searchMembers } from "services/member"
import { verifyResponse } from "utils/httpRequest"

const CLIENT_PAGE_SIZE = 10

// `verifyResponse` only redirects on an invalid session (rtnCode 401) — it never throws
// for ordinary business errors. Since the service layer already unwraps to the response
// body ({ rtnCode, message, data }), a resolved promise here only means "no network error",
// not "the request actually succeeded". Callers must check rtnCode explicitly.
const ensureSuccess = (response) => {
  verifyResponse(response)
  if (response?.rtnCode !== 0) {
    throw new Error(response?.message || "Request failed")
  }
  return response
}

const PractitionerManagement = ({ member }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [clients, setClients] = useState([])
  const [clientPageNo, setClientPageNo] = useState(1)
  const [clientTotal, setClientTotal] = useState(0)
  const [clientSearch, setClientSearch] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [hasSearchedClients, setHasSearchedClients] = useState(false)
  const activeMemberIdRef = useRef(member?.userId)

  const clientPages = useMemo(
    () => Math.max(1, Math.ceil(clientTotal / CLIENT_PAGE_SIZE)),
    [clientTotal],
  )

  // Reset stale state immediately on member switch — otherwise the previous
  // member's enabled/isSuperAdmin values stay on screen until the new fetches resolve.
  useEffect(() => {
    activeMemberIdRef.current = member?.userId
    setEnabled(false)
    setIsSuperAdmin(false)
    setClients([])
    setClientTotal(0)
    setClientPageNo(1)
    setClientSearch("")
    setSearchResults([])
    setHasSearchedClients(false)
  }, [member?.userId])

  const loadClients = useCallback(
    async (pageNo = 1) => {
      const requestedMemberId = member?.userId
      if (!requestedMemberId) return
      try {
        const response = await listClients({
          practitionerMemberId: requestedMemberId,
          pageNo,
          pageSize: CLIENT_PAGE_SIZE,
        })
        ensureSuccess(response)
        if (activeMemberIdRef.current !== requestedMemberId) return
        const list = response?.data?.clients
        const total = Number(response?.data?.total || 0)
        const resolvedPage = Number(response?.data?.pageNo || pageNo)
        const rows = Array.isArray(list) ? list : []

        if (rows.length === 0 && total > 0 && resolvedPage > 1) {
          return loadClients(resolvedPage - 1)
        }

        setClients(rows)
        setClientTotal(total)
        setClientPageNo(resolvedPage)
        setEnabled(true)
      } catch {
        if (activeMemberIdRef.current !== requestedMemberId) return
        setClients([])
        setClientTotal(0)
        setClientPageNo(1)
        setEnabled(false)
      }
    },
    [member?.userId],
  )

  useEffect(() => {
    if (member?.userId) {
      loadClients(1).catch(() => {})
    }
  }, [member?.userId, loadClients])

  useEffect(() => {
    const requestedMemberId = member?.userId
    if (!requestedMemberId) return
    getPractitionerStatus({ memberId: requestedMemberId })
      .then((response) => {
        ensureSuccess(response)
        if (activeMemberIdRef.current !== requestedMemberId) return
        setIsSuperAdmin(!!response?.data?.isSuperAdmin)
      })
      .catch(() => {
        if (activeMemberIdRef.current !== requestedMemberId) return
        setIsSuperAdmin(false)
      })
  }, [member?.userId])

  const handleEnable = async () => {
    if (!member?.userId) return
    setLoading(true)
    try {
      const response = await upsertPractitioner({
        memberId: member.userId,
        practitionerType: "coach",
      })
      ensureSuccess(response)
      setEnabled(true)
      enqueueSnackbar("Practitioner account enabled", { variant: "success" })
      await loadClients(1)
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to enable practitioner account", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const handleDisable = async () => {
    if (!member?.userId) return
    setLoading(true)
    try {
      const response = await disablePractitioner({ memberId: member.userId })
      ensureSuccess(response)
      setEnabled(false)
      setClients([])
      setClientTotal(0)
      setClientPageNo(1)
      enqueueSnackbar("Practitioner account disabled", { variant: "success" })
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to disable practitioner account", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const handleEnableSuperAdmin = async () => {
    if (!member?.userId) return
    setLoading(true)
    try {
      const response = await enableSuperAdmin({ memberId: member.userId })
      ensureSuccess(response)
      setIsSuperAdmin(true)
      setEnabled(true)
      enqueueSnackbar("Super admin access granted", { variant: "success" })
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to grant super admin access", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const handleDisableSuperAdmin = async () => {
    if (!member?.userId) return
    setLoading(true)
    try {
      const response = await disableSuperAdmin({ memberId: member.userId })
      ensureSuccess(response)
      setIsSuperAdmin(false)
      enqueueSnackbar("Super admin access revoked", { variant: "success" })
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to revoke super admin access", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const buildClientSearchParams = (term) => {
    const trimmed = term.trim()
    if (!trimmed) return null
    const looksLikeEmail = trimmed.includes("@")
    return {
      pageNo: 1,
      pageSize: 10,
      searchName: looksLikeEmail ? undefined : trimmed,
      searchEmail: looksLikeEmail ? trimmed : undefined,
    }
  }

  const resolveSearchMemberId = (result) =>
    Number(result?.userId || result?.memberId || result?.id || 0)

  const resolveSearchMemberLabel = (result) => {
    const fullName = [result?.firstName, result?.lastName].filter(Boolean).join(" ").trim()
    return fullName || result?.nickName || result?.communityHandle || result?.email || "Member"
  }

  const handleSearchClients = async () => {
    const params = buildClientSearchParams(clientSearch)
    if (!params) {
      setSearchResults([])
      setHasSearchedClients(false)
      return
    }
    setLoading(true)
    setHasSearchedClients(true)
    try {
      // admin/member/list returns a plain PageResultDto (no rtnCode/data wrapper),
      // unlike the practitioner endpoints — list is at the top level, not response.data.list.
      const response = await searchMembers(params)
      verifyResponse(response)
      const list = response?.list
      setSearchResults(Array.isArray(list) ? list : [])
    } catch (err) {
      setSearchResults([])
      enqueueSnackbar(err?.message || "Failed to search members", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const handleAssignClient = async (targetMemberId) => {
    if (!member?.userId || !targetMemberId) return
    setLoading(true)
    try {
      const response = await assignClient({
        practitionerMemberId: member.userId,
        clientMemberId: Number(targetMemberId),
      })
      ensureSuccess(response)
      enqueueSnackbar("Client assigned", { variant: "success" })
      setClientSearch("")
      setSearchResults([])
      setHasSearchedClients(false)
      await loadClients(1)
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to assign client", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const handleUnassignClient = async (targetMemberId) => {
    if (!member?.userId || !targetMemberId) return
    setLoading(true)
    try {
      const response = await unassignClient({
        practitionerMemberId: member.userId,
        clientMemberId: Number(targetMemberId),
      })
      ensureSuccess(response)
      enqueueSnackbar("Client unassigned", { variant: "success" })
      await loadClients(clientPageNo)
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to unassign client", { variant: "warning" })
    } finally {
      setLoading(false)
    }
  }

  const handleClientPageChange = async (_event, value) => {
    setLoading(true)
    try {
      await loadClients(value)
    } finally {
      setLoading(false)
    }
  }

  if (!member?.userId) return null

  return (
    <>
      <Card className="section-card">
        <CardContent>
          <h3 className="section-title">Practitioner Dashboard Access</h3>
          <Typography className="section-subtitle">
            Enable this member as a practitioner, then assign the clients they can monitor in the practitioner dashboard.
          </Typography>

          {loading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
              <CircularProgress size={24} />
            </div>
          )}

          {isSuperAdmin ? (
            <>
              <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                <Button variant="outlined" color="error" onClick={handleDisableSuperAdmin} disabled={loading}>
                  Remove Super Admin
                </Button>
              </div>
              <Typography color="text.secondary" style={{ marginTop: 8, fontSize: 13 }}>
                This member is a platform Super Admin and sees every member on NeuroGym, no invites needed.
                Remove Super Admin to make this member a regular Practitioner instead.
              </Typography>
            </>
          ) : (
            <>
              <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                {enabled ? (
                  <Button variant="outlined" color="error" onClick={handleDisable} disabled={loading}>
                    Disable Practitioner
                  </Button>
                ) : (
                  <Button variant="contained" color="primary" onClick={handleEnable} disabled={loading}>
                    Enable Practitioner
                  </Button>
                )}
              </div>

              {enabled ? (
                <Typography color="text.secondary" style={{ marginTop: 8, fontSize: 13 }}>
                  This member is a regular Practitioner. Disable Practitioner to grant Super Admin access instead.
                </Typography>
              ) : (
                <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                  <Button variant="contained" color="secondary" onClick={handleEnableSuperAdmin} disabled={loading}>
                    Make Super Admin
                  </Button>
                </div>
              )}
            </>
          )}

          {enabled && !isSuperAdmin && (
            <>
              <Typography style={{ marginTop: 24, marginBottom: 8, fontWeight: 600 }}>
                Assign Client
              </Typography>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  label="Search by name or email"
                  value={clientSearch}
                  onChange={(e) => {
                    const value = e.target.value
                    setClientSearch(value)
                    if (!value.trim()) {
                      setSearchResults([])
                      setHasSearchedClients(false)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleSearchClients()
                    }
                  }}
                  style={{ minWidth: 260 }}
                />
                <Button variant="outlined" onClick={handleSearchClients} disabled={loading}>
                  Search
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                  {searchResults.map((result) => {
                    const targetMemberId = resolveSearchMemberId(result)
                    return (
                    <div
                      key={targetMemberId || result.email}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid var(--cms-border)",
                        borderRadius: 8,
                        padding: "8px 12px",
                      }}
                    >
                      <div>
                        <div>{resolveSearchMemberLabel(result)}</div>
                        <div style={{ fontSize: 12, color: "var(--cms-text-secondary)" }}>{result.email}</div>
                      </div>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAssignClient(targetMemberId)}
                        disabled={loading || !targetMemberId || targetMemberId === member.userId}
                      >
                        Assign
                      </Button>
                    </div>
                    )
                  })}
                </div>
              )}

              {hasSearchedClients && !loading && searchResults.length === 0 && (
                <Typography color="text.secondary" style={{ marginTop: 12, fontSize: 14 }}>
                  No members found for that search.
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {enabled && !isSuperAdmin && (
        <Card className="section-card" style={{ marginTop: 20 }}>
          <CardContent>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              <h3 className="section-title" style={{ margin: 0 }}>
                Assigned Clients
              </h3>
              <Typography color="text.secondary" style={{ fontSize: 14 }}>
                {clientTotal} total
              </Typography>
            </div>

            {clientTotal === 0 ? (
              <Typography color="text.secondary">No clients assigned yet.</Typography>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" aria-label="assigned clients table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow key={client.clientMemberId}>
                          <TableCell>
                            {[client.firstName, client.lastName].filter(Boolean).join(" ") || client.email}
                          </TableCell>
                          <TableCell>{client.email}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleUnassignClient(client.clientMemberId)}
                              disabled={loading}
                            >
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {clientPages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                    <Pagination
                      count={clientPages}
                      page={clientPageNo}
                      onChange={handleClientPageChange}
                      color="primary"
                      disabled={loading}
                    />
                  </div>
                )}

                {clients.length > 0 && (
                  <Typography
                    color="text.secondary"
                    style={{ marginTop: 12, fontSize: 13, textAlign: "center" }}
                  >
                    Showing {(clientPageNo - 1) * CLIENT_PAGE_SIZE + 1}–
                    {(clientPageNo - 1) * CLIENT_PAGE_SIZE + clients.length} of {clientTotal}
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </>
  )
}

export default PractitionerManagement
